import { CURRENT_ENV_ROLE_TYPE } from '../../utils/EnvConfigUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

/**
 * Role theming for every AI interface (chat, summarization, suggested WOs, …),
 * per the design guideline: buyer surfaces are purple, supplier surfaces cyan,
 * with a 2px stroke.
 *
 * Pass the **session's** `userType` (via `useOscarTheme`, which reads
 * `OscarRoleProvider`). `CURRENT_ENV_ROLE_TYPE` is only the last-resort
 * fallback: it defaults to SUPPLIER whenever `REACT_APP_CURRENT_ENV_ROLE_TYPE`
 * is unset, so a plain `yarn start` claims supplier no matter who is signed in.
 *
 * It also cannot live in `tailwind.config.js`: the `prebuild:*` scripts that
 * compile Tailwind do not export `REACT_APP_CURRENT_ENV_ROLE_TYPE` (it is
 * exported inside `build:*`, which runs after), so a role-dependent config would
 * silently ship buyer colours in the supplier build.
 *
 * Class names are written out in full — Tailwind's scanner only sees literals.
 */
export interface OscarThemeClasses {
	/** Panel / card background fill. */
	surface: string;
	/** 2px stroke in the role's accent, per the guideline. */
	stroke: string;
	/** Accent text and the mark's linework. */
	accentText: string;
	/** Hover fill for interactive surfaces sitting on `surface`. */
	surfaceHover: string;
	/** Soft fill behind the mark in the empty state. */
	accentSoft: string;
	/** The panel's leading edge — the strongest accent on the surface. */
	panelEdge: string;
	/** Border around the panel's white body card. */
	panelCardBorder: string;
}

const BUYER_THEME: OscarThemeClasses = {
	surface: 'bg-purple-0',
	stroke: 'border-2 border-solid border-purple-3',
	accentText: 'text-[#54298e]',
	surfaceHover: 'hover:bg-purple-3/20',
	accentSoft: 'bg-purple-2',
	panelEdge: 'border-[#54298e]',
	panelCardBorder: 'border-purple-2',
};

/**
 * Cyan/1 (#F0F7F7) and Cyan/3 (#A7D5D9) per design. `surfaceHover` and
 * `accentSoft` are not in any Figma node — they are derived from the accent so
 * both roles behave the same way.
 */
const SUPPLIER_THEME: OscarThemeClasses = {
	surface: 'bg-cyan-1',
	stroke: 'border-2 border-solid border-cyan-3',
	accentText: 'text-cyan-7',
	surfaceHover: 'hover:bg-cyan-3/20',
	accentSoft: 'bg-cyan-3/50',
	panelEdge: 'border-cyan-7',
	panelCardBorder: 'border-cyan-3',
};

export const getOscarTheme = (roleType?: string): OscarThemeClasses =>
	(roleType || CURRENT_ENV_ROLE_TYPE) === ROLE_TYPES.SUPPLIER ? SUPPLIER_THEME : BUYER_THEME;

export { BUYER_THEME, SUPPLIER_THEME };
